import { useState } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { CalendarBlank, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DAYS = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì'];
const TIME_SLOTS = [];
for (let h = 13; h < 20; h++) { for (let m of [0, 15, 30, 45]) { TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`); } }

export const LessonsList = ({ lessons, onUpdate }) => {
  const [showEdit, setShowEdit] = useState(false);
  const [editLesson, setEditLesson] = useState(null);
  const [form, setForm] = useState({ day: 0, start_time: '13:00', end_time: '13:30' });

  const openEdit = (l) => {
    setEditLesson(l);
    setForm({ day: l.day, start_time: l.start_time, end_time: l.end_time });
    setShowEdit(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await api.updateLesson(editLesson.id, form);
      toast.success('Lezione aggiornata');
      setShowEdit(false);
      if (onUpdate) onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore aggiornamento'); }
  };

  const sorted = [...lessons].sort((a, b) => a.day - b.day || a.start_time.localeCompare(b.start_time));

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold font-['Work_Sans']">Le Mie Lezioni</h2>
        <div className="text-sm text-[#52525B]">{lessons.length} lezioni</div>
      </div>

      {lessons.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm">
          <CalendarBlank size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <p className="text-[#52525B]">Nessuna lezione</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Giorno</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Orario</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Durata</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Alunno</th>
                <th className="text-right p-3 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Modifica</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(l => {
                const [sh, sm] = l.start_time.split(':').map(Number);
                const [eh, em] = l.end_time.split(':').map(Number);
                const dur = (eh * 60 + em) - (sh * 60 + sm);
                return (
                  <tr key={l.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`lesson-row-${l.id}`}>
                    <td className="p-3 text-sm font-medium">{DAYS[l.day] || '-'}</td>
                    <td className="p-3 text-sm text-[#52525B]">{l.start_time} - {l.end_time}</td>
                    <td className="p-3 text-sm text-[#52525B]">{dur} min</td>
                    <td className="p-3 text-sm font-medium uppercase">{l.student_name}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => openEdit(l)} className="p-1.5 rounded hover:bg-[#F4F4F5] text-[#52525B]" title="Modifica" data-testid={`edit-lesson-${l.id}`}>
                        <PencilSimple size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">Modifica Lezione</DialogTitle><DialogDescription>{editLesson?.student_name}</DialogDescription></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <label className="label-overline">Giorno</label>
              <Select value={form.day.toString()} onValueChange={v => setForm({ ...form, day: parseInt(v) })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={i.toString()}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="label-overline">Ora Inizio</label>
                <Select value={form.start_time} onValueChange={v => setForm({ ...form, start_time: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="label-overline">Ora Fine</label>
                <Select value={form.end_time} onValueChange={v => setForm({ ...form, end_time: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{[...TIME_SLOTS, '20:00'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setShowEdit(false)} className="rounded-sm">Annulla</Button><Button type="submit" className="bg-[#0A0A0A] rounded-sm">Aggiorna</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
